import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  enrollContacts,
  exitEnrollment,
  listSequenceEnrollments,
} from "../../../redux/actions/sequencesAction";
import { useSequenceDetail } from "./SequenceDetailContext";
import { ENROLLMENT_STATUS_COLORS } from "./sequenceUtils";

const { Text } = Typography;

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "exited", label: "Exited" },
  { value: "paused_quota", label: "Paused (quota)" },
];

export default function SequenceEnrollments() {
  const { id, isNew, sequenceRecord } = useSequenceDetail();
  const dispatch = useDispatch();
  const enrollmentsList = useSelector((state) => state.sequences.enrollments);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollTag, setEnrollTag] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const fetchEnrollments = useCallback(() => {
    if (isNew) return;
    setLoading(true);
    dispatch(listSequenceEnrollments(id, statusFilter ? { status: statusFilter } : {}))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [dispatch, id, isNew, statusFilter]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const onExit = async (contactId) => {
    try {
      await dispatch(exitEnrollment(id, contactId));
      message.success("Contact exited from the sequence");
      fetchEnrollments();
    } catch (e) {
      message.error("Failed to exit contact");
    }
  };

  const onEnrollByTag = async () => {
    if (!enrollTag.trim()) {
      message.error("Enter a tag.");
      return;
    }
    setEnrolling(true);
    try {
      const result = await dispatch(enrollContacts(id, { tag: enrollTag.trim() }));
      message.success(
        `Enrolled ${result.enrolled} contact(s)${
          result.skipped ? `, skipped ${result.skipped}` : ""
        }.`,
      );
      setEnrollOpen(false);
      setEnrollTag("");
      fetchEnrollments();
    } catch (e) {
      message.error("Failed to enrol contacts");
    } finally {
      setEnrolling(false);
    }
  };

  const columns = [
    { title: "Contact", dataIndex: "emailAddress", key: "emailAddress" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={ENROLLMENT_STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    { title: "Step", dataIndex: "currentStep", key: "currentStep" },
    {
      title: "Next send",
      dataIndex: "nextRunAt",
      key: "nextRunAt",
      render: (v, record) =>
        record.status === "active" && v ? new Date(v).toLocaleString() : "—",
    },
    {
      title: "Enrolled",
      dataIndex: "enrolledAt",
      key: "enrolledAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Exit reason",
      dataIndex: "exitReason",
      key: "exitReason",
      render: (v) => v || "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        record.status === "active" || record.status === "paused_quota" ? (
          <Popconfirm
            title="Exit this contact from the sequence?"
            onConfirm={() => onExit(record.contactId)}
            okText="Exit"
          >
            <Button size="small" danger>
              Exit
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  if (isNew) {
    return (
      <Text type="secondary">Save the sequence first to manage enrolments.</Text>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
        <Select
          style={{ width: 200 }}
          value={statusFilter}
          options={STATUS_FILTER_OPTIONS}
          onChange={setStatusFilter}
        />
        <Button
          icon={<PlusOutlined />}
          disabled={sequenceRecord?.status !== "active"}
          onClick={() => setEnrollOpen(true)}
        >
          Enrol by tag
        </Button>
      </Space>
      {sequenceRecord?.status !== "active" && (
        <Text type="secondary">
          Activate this sequence to enrol contacts manually or via its trigger.
        </Text>
      )}
      <Table
        rowKey={(r) => r._id || r.contactId}
        loading={loading}
        columns={columns}
        dataSource={enrollmentsList}
        pagination={false}
      />
      <Modal
        title="Enrol contacts by tag"
        open={enrollOpen}
        onCancel={() => setEnrollOpen(false)}
        onOk={onEnrollByTag}
        confirmLoading={enrolling}
        okText="Enrol"
      >
        <Text type="secondary">
          Every contact currently carrying this tag is enrolled once, as a
          snapshot — this doesn't keep enrolling new contacts as the tag gets
          applied later.
        </Text>
        <Input
          style={{ marginTop: 12 }}
          value={enrollTag}
          onChange={(e) => setEnrollTag(e.target.value)}
          placeholder="Tag"
        />
      </Modal>
    </Space>
  );
}
